import React, { useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react'
import { useNavigate, Link, useParams } from 'react-router-dom'
import axios from '../../../../config/axios'
import jwtDecode from 'jwt-decode'
import InfiniteSelect from '../../../../components/InfiniteSelect'
import PantoneSelect from '../../../../components/PantoneSelect'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import save from '../../../../assets/certificates/AddParameter/save.svg'
import trash from '../../../../assets/equipment/Tables/trash.svg'
import crosslight from '../../../../assets/stock/FiltersHeader/cross.svg'
import {
  SubHeaderContainer,
  SectionName,
  LeftSide,
} from '../../../../components/styles/subheader-style'
import {
  RegisterCollectionContent,
  MainContainer,
  StyledCard,
  FormGrid,
  StyledFormGroup,
  ToggleSwitchContainer,
  SwitchLabel,
  Slider,
  SwitchInput,
  DeleteButton,
  ActionButtons,
  CancelButton,
  SaveButton,
  BreadcrumbsContainer,
  CardTitle,
  GradeRow,
  GradeHeader,
  GradeLabel,
  GradeOptions,
  SizeButton,
  FamilyTagsContainer,
  FamilyTag,
  RemoveTagButton,
  CollectionFlatSectionHeader,
  CollectionFlatTable,
  CollectionFlatTableHead,
  CollectionFlatGridRow,
  CollectionOutlineAddButton,
  ColorSwatchInline,
} from './style'
import { Plus, Tag, ChevronDown, ChevronUp, Check } from 'lucide-react'
import ApiService from '../../../../services/ApiService'
import { getTagTextColor } from '../../../../utils/colorContrast'

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
const FASHION_API_URL = `${GATEWAY_URL}/backend-fashion-orders/fashion-product`

interface GradeType { id: number; name: string }
interface Grade { id: number; name: string; typeId: number; type?: GradeType; GraduationType?: GradeType }
interface Group { id: number; name: string }
interface GroupItem { id: number; groupId: number; name: string }
interface EpisodeFormItem { localKey: number; serverId?: number; name: string }
interface FamilyFormItem { localKey: number; serverId?: number; name: string }
interface StockOption {
  id: number
  name: string
  code?: string
  colorName?: string
  colorHex?: string
  finishing?: string
}
interface ResourceOption {
  id: number
  name: string
  code?: string
  typeLabel?: string
  colorName?: string
  colorHex?: string
  finishing?: string
}
interface FabricFormRow {
  id: number
  stockId: number | null
  stockLabel: string
  materialId: number | null
  materialName: string
  colorName: string
  colorHex?: string
  code: string
  finishing: string
}
interface TrimFormRow {
  id: number
  resourceId: number | null
  resourceLabel: string
  typeLabel: string
  colorName: string
  colorHex?: string
  code: string
  finishing: string
}

const Breadcrumbs: React.FC<{ items: { label: string; path?: string }[] }> = ({ items }) => (
  <BreadcrumbsContainer>
    <ol>
      {items.map((item, index) => (
        <li key={index}>
          {index === items.length - 1 ? (
            <span>{item.label}</span>
          ) : item.path ? (
            <Link to={item.path}>{item.label}</Link>
          ) : (
            <span>{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </BreadcrumbsContainer>
)

const RegisterCollection: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const numericId = id ? Number(id) : undefined
  const isEditMode = Boolean(id)
  const { setFeedback } = useContext(FeedbackContext)

  const [collectionName, setCollectionName] = useState<string>('')
  const [brand, setBrand] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingData, setLoadingData] = useState<boolean>(true)

  const [gradesByType, setGradesByType] = useState<Record<string, Grade[]>>({})
  const [enabledGradeTypes, setEnabledGradeTypes] = useState<Record<string, boolean>>({})
  const [selectedGradeIds, setSelectedGradeIds] = useState<number[]>([])

  const [allGroups, setAllGroups] = useState<Group[]>([])
  const [groups, setGroups] = useState<GroupItem[]>([])
  const [groupSearch, setGroupSearch] = useState<string>('')
  const [newGroupName, setNewGroupName] = useState<string>('')

  const [episodes, setEpisodes] = useState<EpisodeFormItem[]>([])
  const [episodeSearch, setEpisodeSearch] = useState<string>('')
  const [allEpisodes, setAllEpisodes] = useState<Array<{ id: number; name: string }>>([])
  const episodeLocalKeyRef = useRef(0)

  const [families, setFamilies] = useState<FamilyFormItem[]>([])
  const [familySearch, setFamilySearch] = useState<string>('')
  const [allFamilies, setAllFamilies] = useState<Array<{ id: number; name: string }>>([])
  const familyLocalKeyRef = useRef(0)

  const [showGradesModal, setShowGradesModal] = useState(false)
  const [newGradeType, setNewGradeType] = useState('')
  const [newGradeSizes, setNewGradeSizes] = useState('')
  const [editingGradeId, setEditingGradeId] = useState<number | null>(null)
  const [editingGradeName, setEditingGradeName] = useState('')
  const [gradesLoading, setGradesLoading] = useState(false)

  const [fabrics, setFabrics] = useState<FabricFormRow[]>([])
  const [trims, setTrims] = useState<TrimFormRow[]>([])
  const materialLocalKeyRef = useRef(0)
  const [collectionColors, setCollectionColors] = useState<{ id: number | string; name: string; hex?: string; pantone?: { code: string; name?: string; hex?: string } | null }[]>([])
  const [allColorOptions, setAllColorOptions] = useState<{ id: number | string; name: string; hex?: string; pantone?: { code: string; name?: string; hex?: string } | null }[]>([])
  const [colorUsageByFamily, setColorUsageByFamily] = useState<Record<string, string[]>>({})
  const [colorSearch, setColorSearch] = useState<string>('')
  const [pendingPantone, setPendingPantone] = useState<{ code: string; name?: string; hex?: string } | null>(null)
  const [activeColorIdForPantone, setActiveColorIdForPantone] = useState<number | string | null>(null)

  const normalize = (s: string) => s.trim().toLowerCase()
  const getStockFinishing = (stock: any): string =>
    String(stock?.Finishing?.type ?? stock?.finishing ?? '').trim()
  const addColorSafely = (
    list: { id: number | string; name: string; hex?: string; pantone?: { code: string; name?: string; hex?: string } | null }[],
    item: { id: number | string; name: string; hex?: string; pantone?: { code: string; name?: string; hex?: string } | null }
  ) =>
    list.some((c) => String(c.id) === String(item.id) || normalize(c.name) === normalize(item.name))
      ? list
      : [...list, item]


  useEffect(() => {
    const fetchInitialData = async () => {
      setLoadingData(true)
      const token = localStorage.getItem('token')
      const headers = { Authorization: `Bearer ${token}` }

      let byType: Record<string, Grade[]> = {}
      let enabledTypes: Record<string, boolean> = {}
      try {
        const gradesRes = await ApiService.fashion.findAllGrades()
        const grades: Grade[] = Array.isArray(gradesRes.data) ? gradesRes.data : []
        byType = {}
        enabledTypes = {}
        grades.forEach((grade) => {
          const typeName = (grade.GraduationType || grade.type)?.name
          if (!typeName) return
          if (!byType[typeName]) {
            byType[typeName] = []
            enabledTypes[typeName] = false
          }
          byType[typeName].push(grade)
        })
        Object.keys(byType).forEach((typeName) => {
          byType[typeName].sort((a, b) => {
            const aNum = parseInt(a.name)
            const bNum = parseInt(b.name)
            if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum
            return a.name.localeCompare(b.name)
          })
        })
        setGradesByType(byType)
        setEnabledGradeTypes(enabledTypes)
      } catch (err) {
        console.error('Erro ao carregar grades:', err)
      }

      try {
        const groupsRes = await ApiService.fashion.findAllGroups()
        const groupsData: Group[] = Array.isArray(groupsRes.data) ? groupsRes.data : []
        setAllGroups(groupsData)
      } catch (err) {
        console.error('Erro ao carregar grupos:', err)
      }



      if (isEditMode && numericId) {
        try {
          const collectionRes = await ApiService.fashion.findCollectionById(numericId)
          const collection = collectionRes.data
          setCollectionName(collection.name || '')
          setBrand(collection.brand || '')

          if (collection.CollectionColor && Array.isArray(collection.CollectionColor)) {
            const cols = collection.CollectionColor.map((c: any) => ({
              id: c.id || `${c.name}-${Math.random()}`,
              name: c.name || '',
              hex: c.Pantone?.hex ?? c.hex ?? undefined,
              pantone: c.Pantone ?? null,
            }))
            setCollectionColors(cols)
            setAllColorOptions(cols)
          }

          try {
            const familiesRes = await ApiService.fashion.getCollectionFamilies(numericId)
            const families = Array.isArray(familiesRes.data) ? familiesRes.data : []
            const usage: Record<string, string[]> = {}

            families.forEach((family: any) => {
              const familyName = String(family?.name || '').trim()
              const familyColors = Array.isArray(family?.FamilyColor) ? family.FamilyColor : []
              familyColors.forEach((fc: any) => {
                const colorId = fc?.CollectionColor?.id
                if (typeof colorId !== 'number') return
                const key = String(colorId)
                if (!usage[key]) usage[key] = []
                if (familyName && !usage[key].includes(familyName)) usage[key].push(familyName)
              })
            })

            setColorUsageByFamily(usage)
          } catch (err) {
            console.error('Erro ao carregar uso de cores por família:', err)
            setColorUsageByFamily({})
          }

          const collectionGrades = collection.CollectionGrade || collection.CollectionGrades || collection.GradeInCollection || collection.GradeInProduct || collection.gradeInProduct || collection.gradeInCollection || []
          if (Array.isArray(collectionGrades) && collectionGrades.length > 0) {
            const gradeIdsFromCollection: number[] = collectionGrades
              .map((g: any) => g.gradeId || g.Grade?.id || g.id)
              .map((v: any) => Number(v))
              .filter((n: number) => !Number.isNaN(n) && n > 0)

            if (gradeIdsFromCollection.length > 0) {
              setSelectedGradeIds(gradeIdsFromCollection)

              const typesToEnable: Record<string, boolean> = {}
              const sourceByType = Object.keys(byType).length ? byType : gradesByType
              Object.keys(sourceByType).forEach((typeName) => {
                const gradesForType: Grade[] = sourceByType[typeName] || []
                const hasAny = gradesForType.some((gr: Grade) => gradeIdsFromCollection.includes(Number(gr.id)))
                if (hasAny) typesToEnable[typeName] = true
              })

              setEnabledGradeTypes((prev) => ({ ...prev, ...typesToEnable }))
            }
          }

          const collectionGroups = collection.CollectionGroup || collection.GroupInCollection || collection.GroupInCollections || collection.GroupInCollections || []
          if (Array.isArray(collectionGroups) && collectionGroups.length > 0) {
            const groupItems: GroupItem[] = collectionGroups.map((g: any, idx: number) => ({
              id: idx + 1,
              groupId: Number(g.groupId || g.Group?.id || g.GroupId || g.id) || 0,
              name: (g.Group && (g.Group.name || g.GroupName)) || g.name || ''
            }))
            setGroups(groupItems)
          }

          const rawEpisodes = collection.Episode || collection.Episodes
          if (Array.isArray(rawEpisodes) && rawEpisodes.length > 0) {
            setEpisodes(
              rawEpisodes.map((ep: any) => {
                episodeLocalKeyRef.current += 1
                const sid = ep?.id
                return {
                  localKey: episodeLocalKeyRef.current,
                  serverId: typeof sid === 'number' ? sid : undefined,
                  name: String(ep?.name || ''),
                }
              }),
            )
            setAllEpisodes(
              rawEpisodes.map((ep: any) => ({
                id: ep.id,
                name: String(ep.name || ''),
              }))
            )
          } else {
            setEpisodes([])
            setAllEpisodes([])
          }

          const rawFamilies = collection.Family || collection.Families
          if (Array.isArray(rawFamilies) && rawFamilies.length > 0) {
            setFamilies(
              rawFamilies.map((f: any) => {
                familyLocalKeyRef.current += 1
                const sid = f?.id
                return {
                  localKey: familyLocalKeyRef.current,
                  serverId: typeof sid === 'number' ? sid : undefined,
                  name: String(f?.name || ''),
                }
              }),
            )
            setAllFamilies(
              rawFamilies.map((f: any) => ({
                id: f.id,
                name: String(f.name || ''),
              }))
            )
          } else {
            setFamilies([])
            setAllFamilies([])
          }

          if (collection.CollectionMaterial && Array.isArray(collection.CollectionMaterial)) {
            const fabricRows: FabricFormRow[] = []
            const trimRows: TrimFormRow[] = []
            collection.CollectionMaterial.forEach((mat: any) => {
              const kind = mat.kind === 'TRIM' ? 'TRIM' : 'FABRIC'
              if (kind === 'TRIM') {
                materialLocalKeyRef.current += 1
                const tr = mat.TrimResource
                const ic = tr?.ItemColor
                trimRows.push({
                  id: materialLocalKeyRef.current,
                  resourceId: typeof mat.resourceId === 'number' ? mat.resourceId : tr?.id ?? null,
                  resourceLabel: tr?.name ? buildResourceLabel(tr) : '',
                  typeLabel: tr?.Type_Category_Resource?.name
                    ? String(tr.Type_Category_Resource.name)
                    : tr?.Category_Resource?.name
                      ? String(tr.Category_Resource.name)
                      : '—',
                  colorName: ic?.name ? String(ic.name) : '—',
                  colorHex: ic?.hex ? String(ic.hex) : (ic?.Pantone?.hex ? String(ic.Pantone.hex) : undefined),
                  code: tr?.code != null ? String(tr.code) : '',
                  finishing: tr?.Finishing?.type ? String(tr.Finishing.type) : (tr?.finishing ? String(tr.finishing) : '—'),
                })
                return
              }
              materialLocalKeyRef.current += 1
              const stockId = typeof mat.stockId === 'number' ? mat.stockId : null
              const stock = mat.Stock
              const stockLabel = stockId !== null ? buildStockLabel(stock) : ''
              const materialId = typeof mat.materialId === 'number'
                ? mat.materialId
                : mat.Material?.id || null
              const materialName = mat.Material?.name ? String(mat.Material.name) : ''
              const itemColor = stock?.ItemColor
              const codeStr =
                stock?.Code?.code != null && String(stock.Code.code).trim() !== ''
                  ? String(stock.Code.code)
                  : stock?.codeId != null
                    ? String(stock.codeId)
                    : ''
              fabricRows.push({
                id: materialLocalKeyRef.current,
                stockId,
                stockLabel,
                materialId,
                materialName,
                colorName: itemColor?.name ? String(itemColor.name) : '—',
                colorHex: itemColor?.hex ? String(itemColor.hex) : (itemColor?.Pantone?.hex ? String(itemColor.Pantone.hex) : undefined),
                code: codeStr,
                finishing: getStockFinishing(stock),
              })
            })
            setFabrics(fabricRows)
            setTrims(trimRows)
          }
        } catch (error: any) {
          console.error('Erro ao carregar coleção:', error)
          alert('Erro ao carregar dados da coleção')
        }
      } else {
        setAllEpisodes([])
        setAllFamilies([])
      }

      setLoadingData(false)
    }

    fetchInitialData()
  }, [id, isEditMode])

  const buildStockLabel = useCallback((stock: any): string => {
    if (!stock) return ''
    const materialName = (stock?.Material?.name
      ? String(stock.Material.name)
      : stock?.material
        ? String(stock.material)
        : '').trim()
    const description = (stock?.description ? String(stock.description) : '').trim()
    const codeStr = (stock?.Code?.code != null && String(stock.Code.code).trim() !== ''
      ? String(stock.Code.code)
      : stock?.code != null && String(stock.code).trim() !== ''
        ? String(stock.code)
        : stock?.codeId
          ? String(stock.codeId)
          : '').trim()
    const parts = [materialName]
    if (description && description.toLowerCase() !== materialName.toLowerCase()) {
      parts.push(description)
    }
    if (codeStr) {
      parts.push(`Cód. ${codeStr}`)
    }
    return parts.filter(Boolean).join(' - ') || `Stock ${stock?.id ?? ''}`
  }, [])

  const fetchStocks = useCallback(async (limit: number, offset: number, search?: string) => {
    const token = localStorage.getItem('token')
    const headers = { Authorization: `Bearer ${token}` }
    const params: Record<string, string | number> = {
      limit: Number(limit) || 20,
      offset: Number(offset) || 0,
    }
    const trimmedSearch = (search || '').trim()
    if (trimmedSearch) params.search = trimmedSearch

    const res = await axios.get(`${FASHION_API_URL}/stocks`, { headers, params })
    const list = Array.isArray(res.data) ? res.data : []
    const mapped = list.map((stock: any) => {
      const ic = stock?.ItemColor
      const colorNameRaw =
        (ic?.name != null && String(ic.name).trim() !== '' ? String(ic.name) : '') ||
        (stock?.color != null && String(stock.color).trim() !== '' ? String(stock.color) : '')
      const colorName = colorNameRaw || '—'
      const pantoneHex =
        ic?.Pantone?.hex != null ? String(ic.Pantone.hex) : stock?.pantone?.hex != null ? String(stock.pantone.hex) : undefined
      const colorHex =
        ic?.hex != null && String(ic.hex).trim() !== ''
          ? String(ic.hex)
          : stock?.colorHex != null && String(stock.colorHex).trim() !== ''
            ? String(stock.colorHex)
            : pantoneHex
      const displayCode =
        stock?.Code?.code != null && String(stock.Code.code).trim() !== ''
          ? String(stock.Code.code)
          : stock?.code != null && String(stock.code).trim() !== ''
            ? String(stock.code)
            : stock?.codeId != null
              ? String(stock.codeId)
              : ''
      return {
        id: Number(stock?.id),
        name: buildStockLabel(stock),
        code: displayCode,
        colorName,
        colorHex,
        finishing: getStockFinishing(stock),
        description: stock?.description ? String(stock.description) : undefined,
      }
    })

    const selectedStockIds = new Set(
      fabrics.map((f) => f.stockId).filter((sid): sid is number => sid !== null)
    )
    return mapped.filter((item) => !selectedStockIds.has(item.id))
  }, [buildStockLabel, fabrics])

  const buildResourceLabel = useCallback((resource: any): string => {
    if (!resource) return ''
    const name = (resource?.name ? String(resource.name) : '').trim()
    const description = (resource?.description ? String(resource.description) : '').trim()
    const parts = [name]
    if (description && description.toLowerCase() !== name.toLowerCase()) {
      parts.push(description)
    }
    return parts.filter(Boolean).join(' - ') || `Resource ${resource?.id ?? ''}`
  }, [])

  const fetchResourcesForTrims = useCallback(
    async (limit: number, offset: number, search?: string) => {
      const token = localStorage.getItem('token')
      const headers = { Authorization: `Bearer ${token}` }
      const params: Record<string, string | number> = {
        limit: Number(limit) || 20,
        offset: Number(offset) || 0,
      }
      const trimmedSearch = (search || '').trim()
      if (trimmedSearch) params.search = trimmedSearch

      const res = await axios.get(`${FASHION_API_URL}/resources`, { headers, params })
      const list = Array.isArray(res.data) ? res.data : []
      const mapped = list.map((resource: any) => {
        const ic = resource?.ItemColor
        const colorNameRaw =
          (ic?.name != null && String(ic.name).trim() !== '' ? String(ic.name) : '') ||
          (resource?.color != null && String(resource.color).trim() !== '' ? String(resource.color) : '')
        const colorName = colorNameRaw || '—'
        const colorHex =
          ic?.hex != null && String(ic.hex).trim() !== ''
            ? String(ic.hex)
            : ic?.Pantone?.hex != null
              ? String(ic.Pantone.hex)
              : resource?.colorHex != null
                ? String(resource.colorHex)
                : resource?.pantone?.hex != null
                  ? String(resource.pantone.hex)
                  : undefined
        return {
          id: Number(resource?.id),
          name: buildResourceLabel(resource),
          code: resource?.code != null ? String(resource.code) : '',
          typeLabel: resource?.Type_Category_Resource?.name
            ? String(resource.Type_Category_Resource.name)
            : resource?.Category_Resource?.name
              ? String(resource.Category_Resource.name)
              : '—',
          colorName,
          colorHex,
          finishing: resource?.Finishing?.type ? String(resource.Finishing.type) : (resource?.finishing ? String(resource.finishing) : ''),
          description: resource?.description ? String(resource.description) : undefined,
        }
      })

      const selectedResourceIds = new Set(
        trims.map((t) => t.resourceId).filter((rid): rid is number => rid !== null)
      )
      return mapped.filter((item) => !selectedResourceIds.has(item.id))
    },
    [buildResourceLabel, trims],
  )

  const toggleGradeType = (typeName: string, enabled: boolean) => {
    setEnabledGradeTypes((prev) => ({ ...prev, [typeName]: enabled }))
    if (!enabled) {
      const gradeIdsOfType = gradesByType[typeName]?.map((g) => g.id) || []
      setSelectedGradeIds((prev) => prev.filter((id) => !gradeIdsOfType.includes(id)))
    }
  }

  const toggleGrade = (gradeId: number, typeName: string) => {
    if (!enabledGradeTypes[typeName]) return
    setSelectedGradeIds((prev) => (prev.includes(gradeId) ? prev.filter((id) => id !== gradeId) : [...prev, gradeId]))
  }

  const removeGroup = (id: number) => setGroups((prev) => prev.filter((g) => g.id !== id))
  const removeCollectionColor = (id: number | string) => setCollectionColors((prev) => prev.filter((c) => c.id !== id))

  const removeEpisode = (localKey: number) => {
    setEpisodes((prev) => prev.filter((e) => e.localKey !== localKey))
  }

  const removeFamily = (localKey: number) => {
    setFamilies((prev) => prev.filter((f) => f.localKey !== localKey))
  }

  const availableEpisodeOptions = useMemo(() => {
    const selectedIds = new Set(episodes.map((e) => String(e.serverId || e.name).toLowerCase()))
    const normalizedSearch = episodeSearch.trim().toLowerCase()

    return allEpisodes
      .filter((e) => !selectedIds.has(String(e.id).toLowerCase()) && !selectedIds.has(e.name.toLowerCase()))
      .filter((e) => normalizedSearch ? e.name.toLowerCase().includes(normalizedSearch) : true)
      .map((e) => ({ id: e.id, name: e.name }))
  }, [allEpisodes, episodes, episodeSearch])

  const availableFamilyOptions = useMemo(() => {
    const selectedIds = new Set(families.map((f) => String(f.serverId || f.name).toLowerCase()))
    const normalizedSearch = familySearch.trim().toLowerCase()

    return allFamilies
      .filter((f) => !selectedIds.has(String(f.id).toLowerCase()) && !selectedIds.has(f.name.toLowerCase()))
      .filter((f) => normalizedSearch ? f.name.toLowerCase().includes(normalizedSearch) : true)
      .map((f) => ({ id: f.id, name: f.name }))
  }, [allFamilies, families, familySearch])

  const handleCreateGrades = async () => {
    const typeName = newGradeType.trim()
    const sizesStr = newGradeSizes.trim()
    if (!typeName || !sizesStr) {
      alert('Por favor, preencha o tipo e pelo menos um tamanho.')
      return
    }

    setGradesLoading(true)
    const sizes = sizesStr.split(',').map((s) => s.trim()).filter(Boolean)

    try {
      const createdGrades: Grade[] = []
      for (const size of sizes) {
        const response = await ApiService.fashion.createGrade({ name: size, typeName })
        createdGrades.push(response.data)
      }

      setGradesByType((prev) => {
        const next = { ...prev }
        if (!next[typeName]) {
          next[typeName] = []
        }
        createdGrades.forEach((g) => {
          if (!next[typeName].some((item) => item.id === g.id)) {
            next[typeName].push(g)
          }
        })
        next[typeName].sort((a, b) => {
          const aNum = parseInt(a.name)
          const bNum = parseInt(b.name)
          if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum
          return a.name.localeCompare(b.name)
        })
        return next
      })

      setEnabledGradeTypes((prev) => ({ ...prev, [typeName]: true }))
      setNewGradeSizes('')
    } catch (err: any) {
      console.error('Erro ao cadastrar grades:', err)
      alert(err.response?.data?.message || 'Erro ao cadastrar grades.')
    } finally {
      setGradesLoading(false)
    }
  }

  const handleUpdateGrade = async (gradeId: number, typeName: string) => {
    const newName = editingGradeName.trim()
    if (!newName) return

    try {
      const response = await ApiService.fashion.updateGrade(gradeId, { name: newName })
      const updated: Grade = response.data

      setGradesByType((prev) => {
        const next = { ...prev }
        if (next[typeName]) {
          next[typeName] = next[typeName].map((g) => (g.id === gradeId ? updated : g))
        }
        return next
      })
      setEditingGradeId(null)
    } catch (err: any) {
      console.error('Erro ao atualizar grade:', err)
      alert(err.response?.data?.message || 'Erro ao atualizar grade.')
    }
  }

  const handleDeleteGrade = async (gradeId: number, typeName: string) => {
    if (!confirm('Deseja realmente excluir esta grade? Esta ação não pode ser desfeita.')) return

    try {
      await ApiService.fashion.deleteGrade(gradeId)

      setGradesByType((prev) => {
        const next = { ...prev }
        if (next[typeName]) {
          next[typeName] = next[typeName].filter((g) => g.id !== gradeId)
          if (next[typeName].length === 0) {
            delete next[typeName]
          }
        }
        return next
      })

      setSelectedGradeIds((prev) => prev.filter((id) => id !== gradeId))
    } catch (err: any) {
      console.error('Erro ao excluir grade:', err)
      alert(err.response?.data?.message || 'Não é possível excluir esta grade pois ela está sendo usada.')
    }
  }

  const availableGroupOptions = useMemo(() => {
    const selectedIds = new Set(groups.map((g) => g.groupId))
    const normalizedSearch = groupSearch.trim().toLowerCase()

    return allGroups
      .filter((g) => !selectedIds.has(g.id))
      .filter((g) => normalizedSearch ? g.name.toLowerCase().includes(normalizedSearch) : true)
      .map((g) => ({ id: g.id, name: g.name }))
  }, [allGroups, groups, groupSearch])

  const availableColorOptions = useMemo(() => {
    const selectedIds = new Set(collectionColors.map((c) => String(c.id)))
    const normalizedSearch = colorSearch.trim().toLowerCase()

    return allColorOptions
      .filter((c) => !selectedIds.has(String(c.id)))
      .filter((c) => normalizedSearch ? c.name.toLowerCase().includes(normalizedSearch) : true)
      .map((c) => ({ id: c.id, name: c.name, hex: c.hex, pantone: c.pantone ?? null }))
  }, [allColorOptions, collectionColors, colorSearch])

  const addFabricRow = () => {
    materialLocalKeyRef.current += 1
    const newId = materialLocalKeyRef.current
    setFabrics((prev) => [
      ...prev,
      {
        id: newId,
        stockId: null,
        stockLabel: '',
        materialId: null,
        materialName: '',
        colorName: '—',
        colorHex: undefined,
        code: '',
        finishing: '',
      },
    ])
  }

  const removeFabricRow = (rowId: number) => setFabrics((prev) => prev.filter((m) => m.id !== rowId))

  const addTrimRow = () => {
    materialLocalKeyRef.current += 1
    const newId = materialLocalKeyRef.current
    setTrims((prev) => [
      ...prev,
      {
        id: newId,
        resourceId: null,
        resourceLabel: '',
        typeLabel: '—',
        colorName: '—',
        colorHex: undefined,
        code: '',
        finishing: '',
      },
    ])
  }

  const removeTrimRow = (rowId: number) => setTrims((prev) => prev.filter((m) => m.id !== rowId))

  const selectFabricStock = (fabricItemId: number, stock: StockOption | null) => {
    setFabrics((prev) =>
      prev.map((m) =>
        m.id === fabricItemId
          ? {
              ...m,
              stockId: stock?.id ?? null,
              stockLabel: stock?.name || '',
              materialId: null,
              materialName: '',
              colorName: stock?.colorName || '—',
              colorHex: stock?.colorHex,
              code: stock?.code || '',
              finishing: stock?.finishing || '',
            }
          : m,
      ),
    )
  }

  const selectTrimResource = (trimItemId: number, resource: ResourceOption | null) => {
    setTrims((prev) =>
      prev.map((m) =>
        m.id === trimItemId
          ? {
              ...m,
              resourceId: resource?.id ?? null,
              resourceLabel: resource?.name || '',
              typeLabel: resource?.typeLabel || '—',
              colorName: resource?.colorName || '—',
              colorHex: resource?.colorHex,
              code: resource?.code || '',
              finishing: resource?.finishing || '',
            }
          : m,
      ),
    )
  }



  const addGroup = async () => {
    const nameToCreate = newGroupName?.trim()
    if (!nameToCreate) return
    try {
      const token = localStorage.getItem('token')
      const res = await axios.post(`${FASHION_API_URL}/groups`, { name: nameToCreate }, { headers: { Authorization: `Bearer ${token}` } })
      const created = res.data
      if (created && created.id) {
        const newId = groups.length > 0 ? Math.max(...groups.map((g) => g.id)) + 1 : 1
        setGroups((prev) => [...prev, { id: newId, groupId: created.id, name: created.name }])
        setNewGroupName('')
      }
    } catch (err) {
      console.error('Erro ao criar grupo:', err)
      alert('Falha ao criar grupo')
    }
  }



  const applyPantoneToActiveColor = useCallback((pantone: { code: string; name?: string; hex?: string } | null) => {
    if (!activeColorIdForPantone || !pantone?.code) return
    setCollectionColors((prev) =>
      prev.map((c) =>
        String(c.id) === String(activeColorIdForPantone) ? { ...c, pantone, hex: pantone.hex } : c
      )
    )
    setAllColorOptions((prev) =>
      prev.map((c) =>
        String(c.id) === String(activeColorIdForPantone) ? { ...c, pantone, hex: pantone.hex } : c
      )
    )
    setActiveColorIdForPantone(null)
    setPendingPantone(null)
  }, [activeColorIdForPantone])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!collectionName.trim()) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Por favor, preencha o nome da coleção.',
      })
      return
    }
    if (selectedGradeIds.length === 0) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'É obrigatório selecionar pelo menos uma grade para a coleção.',
      })
      return
    }

    const colorWithoutPantone = collectionColors.find((c) => !c.pantone?.code)
    if (colorWithoutPantone) {
      alert(`A cor "${colorWithoutPantone.name}" precisa ter um Pantone associado.`)
      return
    }
    if (fabrics.length === 0) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'É obrigatório cadastrar pelo menos uma matéria-prima (tecido).',
      })
      return
    }
    const invalidFabric = fabrics.find((m) => !m.stockId && !m.materialId)
    if (invalidFabric) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Cada matéria-prima precisa ter um tecido (estoque) selecionado.',
      })
      return
    }
    setLoading(true)

    const fabricsPayload = fabrics
      .filter((m) => m.stockId || m.materialId)
      .map((m) => {
        const entry: Record<string, unknown> = {}
        if (typeof m.stockId === 'number') entry.stockId = m.stockId
        if (typeof m.materialId === 'number' && m.stockId === null) {
          entry.materialId = m.materialId
        }
        return entry
      })

    const trimsPayload = trims
      .filter((t) => typeof t.resourceId === 'number')
      .map((t) => ({ resourceId: t.resourceId as number }))
    const selectedGroupIds = groups.filter((g) => g.groupId > 0).map((g) => g.groupId)

    const collectionColorsPayload = collectionColors.map((c) => {
      const pantone = c.pantone ? { code: c.pantone.code, name: c.pantone.name } : null
      const isTemp = typeof c.id === 'string' && String(c.id).startsWith('tmp-')
      return {
        id: isTemp ? undefined : (typeof c.id === 'number' ? c.id : undefined),
        name: c.name,
        pantone,
      }
    })

    const payload: Record<string, unknown> = {
      name: collectionName,
      brand: brand.trim() || null,
      gradeIds: selectedGradeIds,
      groupIds: selectedGroupIds,
      fabrics: fabricsPayload,
      trims: trimsPayload,
      collectionColors: collectionColorsPayload,
    }

    if (isEditMode && numericId) {
      payload.episodes = episodes
        .filter((e) => e.name.trim().length > 0)
        .map((e) => ({
          name: e.name.trim(),
          ...(typeof e.serverId === 'number' ? { id: e.serverId } : {}),
        }))
      payload.families = families
        .filter((f) => f.name.trim().length > 0)
        .map((f) => ({
          name: f.name.trim(),
          ...(typeof f.serverId === 'number' ? { id: f.serverId } : {}),
        }))
    } else {
      payload.episodeNames = episodes.map((e) => e.name.trim()).filter((n) => n.length > 0)
      payload.familyNames = families.map((f) => f.name.trim()).filter((n) => n.length > 0)
    }

    try {
      const token = localStorage.getItem('token')
      if (isEditMode && numericId) {
        await ApiService.fashion.updateCollection(numericId, payload)
        setFeedback({ isOpen: true, statusOk: true, message: 'Coleção atualizada com sucesso!' })
      } else {
        await ApiService.fashion.createCollection(payload)
        setFeedback({ isOpen: true, statusOk: true, message: 'Coleção criada com sucesso!' })
      }
      navigate('/panel/collection')
    } catch (err: any) {
      console.error('Erro ao salvar coleção:', err)
      alert(err.response?.data?.message || 'Erro ao salvar coleção. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const breadcrumbItems = [
    { label: 'Produtos manufaturados', path: '/panel/collection' },
    { label: isEditMode ? 'Editar coleção' : 'Cadastrar coleção' },
  ]

  if (loadingData) return (
    <RegisterCollectionContent>
      <MainContainer>
        <p>Carregando dados...</p>
      </MainContainer>
    </RegisterCollectionContent>
  )

  return (
    <RegisterCollectionContent>
      <SubHeaderContainer style={{ margin: '0 -1.2rem 0' }}>
        <LeftSide>
          <SectionName>{isEditMode ? 'Editar coleção' : 'Nova coleção'}</SectionName>
        </LeftSide>
        <ActionButtons>
          <CancelButton type='button' onClick={() => navigate('/panel/collection')}>Cancelar</CancelButton>
          <SaveButton type='button' onClick={handleSubmit} disabled={loading}><img src={save} alt='Salvar' />{loading ? 'Salvando...' : 'Salvar coleção'}</SaveButton>
        </ActionButtons>
      </SubHeaderContainer>

      <Breadcrumbs items={breadcrumbItems} />

      <MainContainer>
        <form onSubmit={handleSubmit}>
          <StyledCard>
            <CardTitle>Geral</CardTitle>
            <FormGrid cols={4}>
              <StyledFormGroup>
                <label htmlFor='collectionName'>Nome da coleção</label>
                <input type='text' id='collectionName' value={collectionName} onChange={(e) => setCollectionName(e.target.value)} placeholder='Digite aqui' autoComplete='off' />
              </StyledFormGroup>

              <StyledFormGroup>
                <label htmlFor='brand'>Marca</label>
                <input type='text' id='brand' value={brand} onChange={(e) => setBrand(e.target.value)} placeholder='Digite aqui' autoComplete='off' />
              </StyledFormGroup>

              <StyledFormGroup>
                <label>Selecionar grupos</label>
                <InfiniteSelect
                  placeholder='Procure grupos'
                  mode='crud'
                  endpointConfig={{ baseUrl: FASHION_API_URL, listPath: '/groups', createPath: '/groups', deletePath: '/groups', searchParam: 'search' }}
                  allowCreate
                  allowDelete
                  showCreateOnlyIfNotExists
                  minCreateLength={2}
                  options={availableGroupOptions}
                  searchValue={groupSearch}
                  onSearchChange={(search) => setGroupSearch(search)}
                  onSelect={(option) => {
                    if (!option) return
                    const selectedId = Number(option.id)
                    if (selectedId && !groups.some((g) => g.groupId === selectedId)) {
                      setGroups((prev) => [...prev, { id: prev.length > 0 ? Math.max(...prev.map(p => p.id)) + 1 : 1, groupId: selectedId, name: option.name }])
                      setGroupSearch('')
                    }
                  }}
                  onItemCreate={async (name: string) => {
                    const response = await ApiService.fashion.createGroup({ name })

                    const created = response.data
                    if (created?.id) {
                      setAllGroups((prev) => prev.some((g) => g.id === created.id) ? prev : [...prev, { id: created.id, name: created.name }])
                    }

                    setGroupSearch('')

                    return { id: created.id, name: created.name }
                  }}
                  onItemDelete={async (id) => {
                    try {
                      const numericGroupId = Number(id)
                      await ApiService.fashion.deleteGroup(numericGroupId)
                      setGroups((prev) => prev.filter((g) => g.groupId !== numericGroupId))
                      setAllGroups((prev) => prev.filter((g) => g.id !== numericGroupId))
                    } catch (err) {
                      console.error('Erro ao excluir grupo via InfiniteSelect:', err)
                      throw err
                    }
                  }}
                  getOptionLabel={(opt) => opt.name}
                />

                {groups.length > 0 && (
                  <FamilyTagsContainer style={{ marginTop: '8px' }}>
                    {groups.map((group) => (
                      <FamilyTag key={group.id}>
                        {group.name}
                        <RemoveTagButton type='button' onClick={() => removeGroup(group.id)}><img src={crosslight} alt='Remover' style={{ width: 10, height: 10 }} /></RemoveTagButton>
                      </FamilyTag>
                    ))}
                  </FamilyTagsContainer>
                )}
              </StyledFormGroup>

              <StyledFormGroup>
                <label>Episódios</label>
                <InfiniteSelect
                  placeholder='Procure ou adicione episódios'
                  mode='crud'
                  endpointConfig={{
                    baseUrl: FASHION_API_URL,
                    listPath: '/episodes',
                    createPath: isEditMode && numericId ? `/collections/${numericId}/episodes` : '/episodes',
                    deletePath: '/episodes',
                    searchParam: 'search'
                  }}
                  allowCreate
                  allowDelete
                  showCreateOnlyIfNotExists
                  minCreateLength={2}
                  options={availableEpisodeOptions}
                  searchValue={episodeSearch}
                  onSearchChange={(search) => setEpisodeSearch(search)}
                  onSelect={(option) => {
                    if (!option) return
                    const selectedId = Number(option.id)
                    const optionName = option.name.trim()
                    if (!episodes.some((e) => e.name.toLowerCase() === optionName.toLowerCase())) {
                      episodeLocalKeyRef.current += 1
                      setEpisodes((prev) => [
                        ...prev,
                        {
                          localKey: episodeLocalKeyRef.current,
                          serverId: Number.isFinite(selectedId) && selectedId > 0 ? selectedId : undefined,
                          name: optionName,
                        },
                      ])
                      setEpisodeSearch('')
                    }
                  }}
                  onItemCreate={async (name: string) => {
                    const trimmedName = name.trim()
                    if (isEditMode && numericId) {
                      const response = await ApiService.fashion.createEpisode(numericId, { name: trimmedName })
                      const created = response.data
                      if (created?.id) {
                        setAllEpisodes((prev) =>
                          prev.some((e) => e.id === created.id) ? prev : [...prev, { id: created.id, name: created.name }]
                        )
                      }
                      setEpisodeSearch('')
                      return { id: created.id, name: created.name }
                    } else {
                      const tempId = `tmp-ep-${Date.now()}`
                      const newItem = { id: tempId as any, name: trimmedName }
                      setAllEpisodes((prev) => [...prev, newItem])
                      setEpisodeSearch('')
                      return newItem
                    }
                  }}
                  onItemDelete={async (id) => {
                    try {
                      const numericEpisodeId = Number(id)
                      if (Number.isFinite(numericEpisodeId) && !String(id).startsWith('tmp-')) {
                        await ApiService.fashion.deleteEpisode(numericEpisodeId)
                        setEpisodes((prev) => prev.filter((e) => e.serverId !== numericEpisodeId))
                        setAllEpisodes((prev) => prev.filter((e) => e.id !== numericEpisodeId))
                      } else {
                        setEpisodes((prev) => prev.filter((e) => e.name.toLowerCase() !== String(id).toLowerCase()))
                        setAllEpisodes((prev) => prev.filter((e) => String(e.id) !== String(id)))
                      }
                    } catch (err) {
                      console.error('Erro ao excluir episódio via InfiniteSelect:', err)
                      throw err
                    }
                  }}
                  getOptionLabel={(opt) => opt.name}
                />

                {episodes.length > 0 && (
                  <FamilyTagsContainer style={{ marginTop: '8px' }}>
                    {episodes.map((ep) => (
                      <FamilyTag key={ep.localKey}>
                        {ep.name}
                        <RemoveTagButton type='button' onClick={() => removeEpisode(ep.localKey)}>
                          <img src={crosslight} alt='Remover' style={{ width: 10, height: 10 }} />
                        </RemoveTagButton>
                      </FamilyTag>
                    ))}
                  </FamilyTagsContainer>
                )}
              </StyledFormGroup>

              <StyledFormGroup>
                <label>Famílias</label>
                <InfiniteSelect
                  placeholder='Procure ou adicione famílias'
                  mode='crud'
                  endpointConfig={{
                    baseUrl: FASHION_API_URL,
                    listPath: '/families',
                    createPath: isEditMode && numericId ? `/collections/${numericId}/families` : '/families',
                    deletePath: '/families',
                    searchParam: 'search'
                  }}
                  allowCreate
                  allowDelete
                  showCreateOnlyIfNotExists
                  minCreateLength={2}
                  options={availableFamilyOptions}
                  searchValue={familySearch}
                  onSearchChange={(search) => setFamilySearch(search)}
                  onSelect={(option) => {
                    if (!option) return
                    const selectedId = Number(option.id)
                    const optionName = option.name.trim()
                    if (!families.some((f) => f.name.toLowerCase() === optionName.toLowerCase())) {
                      familyLocalKeyRef.current += 1
                      setFamilies((prev) => [
                        ...prev,
                        {
                          localKey: familyLocalKeyRef.current,
                          serverId: Number.isFinite(selectedId) && selectedId > 0 ? selectedId : undefined,
                          name: optionName,
                        },
                      ])
                      setFamilySearch('')
                    }
                  }}
                  onItemCreate={async (name: string) => {
                    const trimmedName = name.trim()
                    if (isEditMode && numericId) {
                      const response = await ApiService.fashion.createFamily({ name: trimmedName, collectionId: numericId })
                      const created = response.data
                      if (created?.id) {
                        setAllFamilies((prev) =>
                          prev.some((f) => f.id === created.id) ? prev : [...prev, { id: created.id, name: created.name }]
                        )
                      }
                      setFamilySearch('')
                      return { id: created.id, name: created.name }
                    } else {
                      const tempId = `tmp-fam-${Date.now()}`
                      const newItem = { id: tempId as any, name: trimmedName }
                      setAllFamilies((prev) => [...prev, newItem])
                      setFamilySearch('')
                      return newItem
                    }
                  }}
                  onItemDelete={async (id) => {
                    try {
                      const numericFamilyId = Number(id)
                      if (Number.isFinite(numericFamilyId) && !String(id).startsWith('tmp-')) {
                        await ApiService.fashion.deleteFamily(numericFamilyId)
                        setFamilies((prev) => prev.filter((f) => f.serverId !== numericFamilyId))
                        setAllFamilies((prev) => prev.filter((f) => f.id !== numericFamilyId))
                      } else {
                        setFamilies((prev) => prev.filter((f) => f.name.toLowerCase() !== String(id).toLowerCase()))
                        setAllFamilies((prev) => prev.filter((f) => String(f.id) !== String(id)))
                      }
                    } catch (err) {
                      console.error('Erro ao excluir família via InfiniteSelect:', err)
                      throw err
                    }
                  }}
                  getOptionLabel={(opt) => opt.name}
                />

                {families.length > 0 && (
                  <FamilyTagsContainer style={{ marginTop: '8px' }}>
                    {families.map((fam) => (
                      <FamilyTag key={fam.localKey}>
                        {fam.name}
                        <RemoveTagButton type='button' onClick={() => removeFamily(fam.localKey)}>
                          <img src={crosslight} alt='Remover' style={{ width: 10, height: 10 }} />
                        </RemoveTagButton>
                      </FamilyTag>
                    ))}
                  </FamilyTagsContainer>
                )}
              </StyledFormGroup>

              <StyledFormGroup>
                <label>Cores da coleção</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <InfiniteSelect
                      placeholder={'Cores da coleção'}
                      searchPlaceholder='Buscar cor...'
                      mode='crud'
                      options={availableColorOptions}
                      searchValue={colorSearch}
                      onSearchChange={(search) => setColorSearch(search)}
                      endpointConfig={{
                        baseUrl: FASHION_API_URL,
                        listPath: '/colors',
                        searchParam: 'search',
                        additionalParams: isEditMode && id ? { collectionId: id } : {},
                        transformResponse: (data: any) => {
                          const cols = Array.isArray(data) ? data : []
                          return cols.map((c: any) => ({
                            id: c.id,
                            name: c.name,
                            pantone: c.Pantone ?? c.pantone ?? null,
                          }))
                        },
                      }}
                      allowCreate
                      allowDelete
                      showCreateOnlyIfNotExists
                      minCreateLength={2}
                      onSelect={(option) => {
                        if (!option) return
                        const colorItem = { id: option.id, name: option.name, hex: option.hex, pantone: option.pantone ?? null }
                        setCollectionColors((prev) => addColorSafely(prev, colorItem))
                        setAllColorOptions((prev) => addColorSafely(prev, colorItem))

                        if (option.pantone?.code) {
                          setPendingPantone(option.pantone)
                          setActiveColorIdForPantone(null)
                        } else {
                          setPendingPantone(null)
                          setActiveColorIdForPantone(option.id)
                        }
                        setColorSearch('')
                      }}
                      onItemCreate={async (name: string) => {
                        const trimmedName = String(name || '').trim()
                        if (!trimmedName) {
                          throw new Error('Nome da cor é obrigatório')
                        }

                        const newItem: { id: number | string; name: string } = { id: `new-${Date.now()}-${Math.random()}`, name: trimmedName }

                        setAllColorOptions((prev) => [...prev, newItem])
                        setColorSearch('')

                        return newItem as any
                      }}
                      onItemDelete={async (itemId) => {
                        const familiesUsingColor = colorUsageByFamily[String(itemId)] || []
                        if (familiesUsingColor.length > 0) {
                          const colorName = collectionColors.find((c) => String(c.id) === String(itemId))?.name || 'esta cor'
                          throw new Error(`Não é possível remover ${colorName}, pois está vinculada à(s) família(s): ${familiesUsingColor.join(', ')}`)
                        }

                        const numericColorId = Number(itemId)
                        if (isEditMode && numericId && Number.isFinite(numericColorId)) {
                          await ApiService.fashion.deleteCollectionColor(numericId, numericColorId)
                        }

                        setAllColorOptions((prev) => prev.filter((c) => String(c.id) !== String(itemId)))
                        setCollectionColors((prev) => prev.filter((c) => String(c.id) !== String(itemId)))
                        setColorUsageByFamily((prev) => {
                          const next = { ...prev }
                          delete next[String(itemId)]
                          return next
                        })
                      }}
                      getOptionLabel={(opt) => opt.name}
                    />
                  </div>
                </div>


                {collectionColors.length > 0 && (
                  <FamilyTagsContainer style={{ marginTop: '8px' }}>
                    {collectionColors.map((c) => {
                      const isActive = String(c.id) === String(activeColorIdForPantone)
                      const hasPantone = !!c.pantone?.code
                      const isExisting = typeof c.id === 'number'
                      return (
                        <FamilyTag
                          key={c.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            cursor: !hasPantone ? 'pointer' : 'default',
                            outline: isActive ? '2px solid #3b82f6' : 'none',
                            outlineOffset: 1,
                            borderRadius: 6,
                          }}
                          onClick={() => {
                            if (!hasPantone) {
                              setActiveColorIdForPantone(c.id)
                              setPendingPantone(null)
                            }
                          }}
                        >
                          {c.pantone?.hex && (
                            <span
                              style={{
                                display: 'inline-block',
                                width: 12,
                                height: 12,
                                borderRadius: 3,
                                backgroundColor: c.pantone.hex.startsWith('#') ? c.pantone.hex : `#${c.pantone.hex}`,
                                border: '1px solid rgba(0,0,0,0.15)',
                                flexShrink: 0
                              }}
                              title={c.pantone.hex} />
                          )}
                          {c.name}
                          {hasPantone && (
                            <span style={{ fontSize: 10, color: '#888', marginLeft: 2 }}>({c.pantone!.code})</span>
                          )}
                          {!hasPantone && (
                            <span style={{ fontSize: 10, color: '#e53e3e', marginLeft: 2 }}>sem Pantone</span>
                          )}
                          <RemoveTagButton onClick={(e) => { e.stopPropagation(); removeCollectionColor(c.id) }}><img src={crosslight} alt='Remover' style={{ width: 10, height: 10 }} /></RemoveTagButton>
                        </FamilyTag>
                      )
                    })}
                  </FamilyTagsContainer>
                )}

                {/* Campo Pantone — aparece para a cor ativa que ainda não tem Pantone */}
                {activeColorIdForPantone && (() => {
                  const activeColor = collectionColors.find((c) => String(c.id) === String(activeColorIdForPantone))
                  if (!activeColor || activeColor.pantone?.code) return null
                  return (
                    <div style={{ marginTop: 8, padding: 12, backgroundColor: '#f0f7ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
                      <label style={{ fontSize: 12, color: '#374151', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                        Atribuir Pantone para <strong>{activeColor.name}</strong> <span style={{ color: '#e53e3e' }}>*</span>
                      </label>
                      <PantoneSelect
                        placeholder='Buscar código Pantone...'
                        value={pendingPantone}
                        onSelect={(pantone) => {
                          if (pantone) {
                            applyPantoneToActiveColor(pantone)
                          } else {
                            setPendingPantone(null)
                          }
                        }}
                      />
                    </div>
                  )
                })()}
              </StyledFormGroup>
            </FormGrid>
          </StyledCard>

          <StyledCard>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <CardTitle style={{ marginBottom: 0 }}>Grades</CardTitle>
              <button
                type='button'
                onClick={() => setShowGradesModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  backgroundColor: '#ebebeb',
                  border: 'none',
                  borderRadius: '100px',
                  padding: '6px 12px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#333',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#dcdcdc')}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#ebebeb')}
              >
                <Plus size={14} />
                Gerenciar grades
              </button>
            </div>
            {Object.keys(gradesByType).length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: 14 }}>Nenhuma grade cadastrada no sistema.</p>
            ) : (
              Object.entries(gradesByType).map(([typeName, grades]) => (
                <GradeRow key={typeName}>
                  <GradeHeader>
                    <ToggleSwitchContainer>
                      <SwitchLabel htmlFor={`grade-${typeName}`}>
                        <SwitchInput type='checkbox' id={`grade-${typeName}`} checked={enabledGradeTypes[typeName] || false} onChange={(e) => toggleGradeType(typeName, e.target.checked)} />
                        <Slider className='slider' />
                      </SwitchLabel>
                    </ToggleSwitchContainer>
                    <GradeLabel>{typeName}</GradeLabel>
                  </GradeHeader>
                  <GradeOptions>
                    {grades.map((grade) => (
                      <SizeButton key={grade.id} type='button' disabled={!enabledGradeTypes[typeName]} className={selectedGradeIds.includes(grade.id) ? 'selected' : ''} onClick={() => toggleGrade(grade.id, typeName)}>{grade.name}</SizeButton>
                    ))}
                  </GradeOptions>
                </GradeRow>
              ))
            )}
          </StyledCard>

          <StyledCard>
            <CollectionFlatSectionHeader>
              <h3>Matérias-primas / Tecidos</h3>
            </CollectionFlatSectionHeader>

            <CollectionOutlineAddButton type='button' onClick={addFabricRow}>
              <Plus size={18} />
              Adicionar item
            </CollectionOutlineAddButton>

            <CollectionFlatTable>
              <CollectionFlatTableHead $columns='minmax(140px, 1fr) minmax(220px, 2fr) 130px 120px 52px'>
                <span>Cor</span>
                <span>Matéria-prima</span>
                <span>Acabamento</span>
                <span>Código</span>
                <span />
              </CollectionFlatTableHead>
              {fabrics.map((row) => (
                <CollectionFlatGridRow key={row.id} $columns='minmax(140px, 1fr) minmax(220px, 2fr) 130px 120px 52px'>
                  <div>
                    <ColorSwatchInline $hex={row.colorHex}>{row.colorName || '—'}</ColorSwatchInline>
                  </div>
                  <div>
                    <InfiniteSelect
                      placeholder='Buscar tecido / estoque'
                      mode='readonly'
                      triggerVariant='cell'
                      fetchFunction={fetchStocks}
                      value={
                        row.stockId
                          ? { id: row.stockId, name: row.stockLabel }
                          : row.materialId
                            ? { id: row.materialId, name: row.materialName }
                            : null
                      }
                      onSelect={(option) => selectFabricStock(row.id, option as StockOption | null)}
                      getOptionLabel={(opt) => opt.name}
                    />
                  </div>
                  <div style={{ fontSize: 13, color: '#374151' }}>{row.finishing || '—'}</div>
                  <div style={{ fontSize: 13, color: '#374151' }}>{row.code || '—'}</div>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <DeleteButton type='button' onClick={() => removeFabricRow(row.id)}>
                      <img src={trash} alt='Excluir' />
                    </DeleteButton>
                  </div>
                </CollectionFlatGridRow>
              ))}
            </CollectionFlatTable>

            <CollectionFlatSectionHeader style={{ marginTop: 28 }}>
              <h3>Insumos / aviamentos</h3>
            </CollectionFlatSectionHeader>

            <CollectionOutlineAddButton type='button' onClick={addTrimRow}>
              <Plus size={18} />
              Adicionar item
            </CollectionOutlineAddButton>

            <CollectionFlatTable>
              <CollectionFlatTableHead $columns='minmax(140px, 1fr) 120px minmax(200px, 2fr) 120px 110px 52px'>
                <span>Cor</span>
                <span>Tipo</span>
                <span>Nome</span>
                <span>Acabamento</span>
                <span>Código</span>
                <span />
              </CollectionFlatTableHead>
              {trims.map((row) => (
                <CollectionFlatGridRow key={row.id} $columns='minmax(140px, 1fr) 120px minmax(200px, 2fr) 120px 110px 52px'>
                  <div>
                    <ColorSwatchInline $hex={row.colorHex}>{row.colorName || '—'}</ColorSwatchInline>
                  </div>
                  <div style={{ fontSize: 13, color: '#374151' }}>{row.typeLabel || '—'}</div>
                  <div>
                    <InfiniteSelect
                      placeholder='Buscar insumo'
                      mode='readonly'
                      triggerVariant='cell'
                      fetchFunction={fetchResourcesForTrims}
                      value={row.resourceId ? { id: row.resourceId, name: row.resourceLabel } : null}
                      onSelect={(option) => selectTrimResource(row.id, option as ResourceOption | null)}
                      getOptionLabel={(opt) => opt.name}
                    />
                  </div>
                  <div style={{ fontSize: 13, color: '#374151' }}>{row.finishing || '—'}</div>
                  <div style={{ fontSize: 13, color: '#374151' }}>{row.code || '—'}</div>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <DeleteButton type='button' onClick={() => removeTrimRow(row.id)}>
                      <img src={trash} alt='Excluir' />
                    </DeleteButton>
                  </div>
                </CollectionFlatGridRow>
              ))}
            </CollectionFlatTable>
          </StyledCard>
        </form>

        {showGradesModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10000,
            }}
            onClick={() => setShowGradesModal(false)}
          >
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                width: '600px',
                maxHeight: '85vh',
                overflowY: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f3f4f6', paddingBottom: '12px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', margin: 0 }}>Gerenciar Grades</h3>
                <button
                  type='button'
                  onClick={() => setShowGradesModal(false)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#9ca3af',
                  }}
                >
                  <img src={crosslight} alt='Fechar' style={{ width: 14, height: 14 }} />
                </button>
              </div>

              <div style={{ backgroundColor: '#f9fafb', padding: '16px', borderRadius: '12px', border: '1px solid #f3f4f6' }}>
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginTop: 0, marginBottom: '12px' }}>Nova Grade / Tamanhos</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 500, color: '#4b5563' }}>Tipo (ex: Adulto, Infantil)</label>
                      <input
                        type='text'
                        value={newGradeType}
                        onChange={(e) => setNewGradeType(e.target.value)}
                        placeholder='Digite ou selecione o tipo'
                        list='grade-types-list'
                        style={{
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '14px',
                        }}
                      />
                      <datalist id='grade-types-list'>
                        {Object.keys(gradesByType).map((t) => (
                          <option key={t} value={t} />
                        ))}
                      </datalist>
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 500, color: '#4b5563' }}>Tamanhos (separados por vírgula)</label>
                      <input
                        type='text'
                        value={newGradeSizes}
                        onChange={(e) => setNewGradeSizes(e.target.value)}
                        placeholder='ex: P, M, G, GG'
                        style={{
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '14px',
                        }}
                      />
                    </div>
                  </div>
                  <button
                    type='button'
                    onClick={handleCreateGrades}
                    disabled={gradesLoading}
                    style={{
                      backgroundColor: '#111827',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: '14px',
                      textAlign: 'center',
                    }}
                  >
                    {gradesLoading ? 'Cadastrando...' : 'Cadastrar Grades'}
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', margin: 0 }}>Grades Cadastradas</h4>
                {Object.keys(gradesByType).length === 0 ? (
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>Nenhuma grade cadastrada.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '40vh', overflowY: 'auto' }}>
                    {Object.entries(gradesByType).map(([typeName, grades]) => (
                      <div key={typeName} style={{ borderBottom: '1px solid #f3f4f6', paddingBottom: '12px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>{typeName}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {grades.map((grade) => (
                            <div
                              key={grade.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                backgroundColor: '#f3f4f6',
                                borderRadius: '6px',
                                padding: '4px 8px',
                                fontSize: '13px',
                                color: '#374151',
                                border: '1px solid #e5e7eb',
                              }}
                            >
                              {editingGradeId === grade.id ? (
                                <input
                                  type='text'
                                  value={editingGradeName}
                                  onChange={(e) => setEditingGradeName(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleUpdateGrade(grade.id, typeName)
                                    if (e.key === 'Escape') setEditingGradeId(null)
                                  }}
                                  autoFocus
                                  style={{
                                    border: '1px solid #3b82f6',
                                    borderRadius: '3px',
                                    padding: '2px 4px',
                                    width: '50px',
                                    fontSize: '12px',
                                  }}
                                />
                              ) : (
                                <span onClick={() => { setEditingGradeId(grade.id); setEditingGradeName(grade.name) }} style={{ cursor: 'pointer', fontWeight: 500 }} title="Clique para editar">{grade.name}</span>
                              )}
                              
                              <div style={{ display: 'flex', gap: '2px' }}>
                                {editingGradeId === grade.id ? (
                                  <button
                                    type='button'
                                    onClick={() => handleUpdateGrade(grade.id, typeName)}
                                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px' }}
                                  >
                                    <Check size={12} style={{ color: '#10b981' }} />
                                  </button>
                                ) : null}
                                <button
                                  type='button'
                                  onClick={() => handleDeleteGrade(grade.id, typeName)}
                                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px' }}
                                  title="Excluir"
                                >
                                  <img src={trash} alt='Excluir' style={{ width: 12, height: 12 }} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </MainContainer>

    </RegisterCollectionContent>
  )
}

export default RegisterCollection